/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from "react";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import DatatypeSelector from "./DatatypeSelector";
import QuantityEntry, { displayStringFromFHIRQuantity } from "./QuantityEntry";
import RangeEntry, { displayStringFromFHIRRange } from "./RangeEntry";
import TimingEntry, { displayStringFromFHIRTiming } from "./TimingEntry";
import UCUM from "../dictionaries/UCUM";
import ArrayEntry from "./ArrayEntry";
import BooleanEntry from "./BooleanEntry";
import StringEntry from "./StringEntry";
import IntegerEntry from "./IntegerEntry";
import MarkdownEntry from "./MarkdownEntry";
import ExpressionEntry, { displayStringFromFHIRExpression } from "./ExpressionEntry";
import CodeEntry from "./CodeEntry";
import PeriodEntry from "./PeriodEntry";
import RelativeTimeEntry, { displayStringFromFHIRRelativeTime } from "./RelativeTimeEntry";

//expected input of Ratio datatype (object with {numerator:Quantity, denominator:SimpleQuantity}) SimpleQuantity = Quantity without comparator
//expected output if matching data structure with matching datatypes is "[ {numerator} / {denominator} ]"
//expected output if matching data structure but missing numerator is "[ [No Numerator] / {denominator} ]"
//expected output if matching data structure but missing denominator is "[ {numerator} / [No Denominator] ]"
//expected output if matching data structure but not matching datatypes is "[ERROR: Ratio data not interpretable.]"
//expected output if string datatype is "{ratio} [ERROR: Ratio data not in expected Ratio datatype.]"
//expected output if missing (undefined/null/'') is ''
//expected output if NaN, empty {}, empty [], or [a,b] is "[ERROR: Ratio data not interpretable.]"
//expected output if not otherwise specified is "[ERROR: Ratio data not interpretable.]"
const getRatioDisplay = (ratio) => {
  if (ratio === undefined || ratio === null || ratio === '') {
    return ""
  }
  if (typeof ratio === "object" && !Array.isArray(ratio) && (ratio.numerator || ratio.denominator)) {
    try {
      let numeratorDisplay = displayStringFromFHIRQuantity(ratio.numerator);
      let denominatorDisplay = displayStringFromFHIRQuantity(ratio.denominator);
      return `[ ${numeratorDisplay ? numeratorDisplay : "[No Numerator]"} / ${denominatorDisplay ? denominatorDisplay : "[No Denominator]"} ]`;
    } catch (e) {
      console.log("ERROR in getRatioDisplay");
      console.log(e);
    }
  }
  if (typeof ratio === "string") {
    return `${ratio} [ERROR: Ratio data not in expected Ratio datatype.]`
  }
  return "[ERROR: Ratio data not interpretable.]";
}

const getDoseAndRateDisplay = (doseAndRate) => {
  let doseAndRateDisplay = "";
  if (doseAndRate.doseRange) {
    doseAndRateDisplay = displayStringFromFHIRRange(doseAndRate.doseRange);
  }
  if (doseAndRate.doseQuantity) {
    doseAndRateDisplay = displayStringFromFHIRQuantity(doseAndRate.doseQuantity);
  }
  if (doseAndRate.doseExpression) {
    doseAndRateDisplay = displayStringFromFHIRExpression(doseAndRate.doseExpression);
  }
  if (doseAndRate.type) {
    doseAndRateDisplay += " [" + displayStringFromFHIRCodeableConcept(doseAndRate.type) + "]";
  }
  if (doseAndRate.rateRatio || doseAndRate.rateRange || doseAndRate.rateQuantity || doseAndRate.rateExpression) {
    if (doseAndRateDisplay) {
      doseAndRateDisplay += " ";
    }
    if (doseAndRate.rateRatio) {
      doseAndRateDisplay += getRatioDisplay(doseAndRate.rateRatio);
    }
    if (doseAndRate.rateRange) {
      doseAndRateDisplay += displayStringFromFHIRRange(doseAndRate.rateRange);
    }
    if (doseAndRate.rateQuantity) {
      doseAndRateDisplay += displayStringFromFHIRQuantity(doseAndRate.rateQuantity);
    }
    if (doseAndRate.rateExpression) {
      doseAndRateDisplay += displayStringFromFHIRExpression(doseAndRate.rateExpression);
    }
  }
  return doseAndRateDisplay;
}

//expected input of Dosage datatype (object with {sequence:integer if R5, condition:DosageCondition, text:string, 
//       additionalInstruction:[CodeableConcept], patientInstruction:string, timing:Timing, asNeeded:boolean, 
//       asNeededFor:[CodeableConcept], site:CodeableConcept,route:CodeableConcept, method:CodeableConcept,
//       doseAndRate: [{type:CodeableConcept, dose[x]:Range/SimpleQuantity, rate[x]:Ratio/Range/SimpleQuantity}], 
//       maxDosePerPeriod:[Ratio] if R5, maxDosePerAdministration:SimpleQuantity if R5, maxDosePerLifetime:SimpleQuantity if R5,
//       safety: DosageSafety })
//expected output if matching data structure with matching datatypes is noted in the code script
//expected output if string datatype is "{dosage} [ERROR: Dosage data not in expected Dosage datatype.]"
//expected output if missing (undefined/null/''/{}) is ''
//expected output if NaN, empty [], or [a,b] is "[ERROR: Dosage data not interpretable.]"
//expected output if not otherwise specified is "[ERROR: Dosage data not interpretable.]"
const displayStringFromFHIRDosage = (dosage) => {
  if (dosage === undefined || dosage === null || dosage === '') {
    return ""
  }
  if (Array.isArray(dosage)) {
    return "[ERROR: Dosage data not interpretable.]";
  }
  if (typeof dosage === 'object') {
    let dosageDisplay = "";
    if (dosage.sequence) {
      dosageDisplay = "Dosage Sequence #" + dosage.sequence + ": ";
    }
    if (dosage.condition) {
      dosageDisplay = "if " + getDosageConditionArrayDisplay(dosage.condition) + ": ";
    }
    if (dosage.text) {
      dosageDisplay += dosage.text;
      return dosageDisplay;
    }
    if (dosage.method) {
      dosageDisplay += displayStringFromFHIRCodeableConcept(dosage.method) + " ";
    }
    if (dosage.doseAndRate?.length > 0) {
      dosageDisplay += dosage.doseAndRate.map((item, index) => getDoseAndRateDisplay(item)).join(', ');
    }
    if (dosage.route) {
      dosageDisplay += " via " + displayStringFromFHIRCodeableConcept(dosage.route);
    }
    if (dosage.site) {
      dosageDisplay += " applied to " + displayStringFromFHIRCodeableConcept(dosage.site);
    }
    if (dosage.asNeeded) {
      dosageDisplay += " as needed"
    }
    if (dosage.asNeededFor?.length > 0) {
      dosageDisplay += " as needed for " + dosage.asNeededFor.map((item, index) => displayStringFromFHIRCodeableConcept(item)).join(' -OR- ');
    }
    if (dosage.timing) {
      dosageDisplay += " " + displayStringFromFHIRTiming(dosage.timing);
    }
    if (dosage.maxDosePerPeriod?.length > 0) {
      dosageDisplay += "; MAXIMUM LIMIT " + dosage.maxDosePerPeriod.map((item, index) => getRatioDisplay(item)).join(', ');
    }
    if (dosage.maxDosePerAdministration) {
      dosageDisplay += "; MAXIMUM LIMIT PER DOSE " + displayStringFromFHIRQuantity(dosage.maxDosePerAdministration);
    }
    if (dosage.maxDosePerLifetime) {
      dosageDisplay += "; MAXIMUM CUMULATIVE LIMIT " + displayStringFromFHIRQuantity(dosage.maxDosePerLifetime);
    }
    if (dosage.additionalInstruction?.length > 0) {
      dosageDisplay += "; " + dosage.additionalInstruction.map((item, index) => displayStringFromFHIRCodeableConcept(item)).join('; ');
    }
    if (dosage.patientInstruction) {
      dosageDisplay += "; " + dosage.patientInstruction;
    }
    if (dosage.safety) {
      dosageDisplay += "; " + displayStringFromFHIRDosageSafety(dosage.safety);
    }
    if (dosageDisplay && typeof dosageDisplay === 'string') {
      return dosageDisplay;
    }
    if (dosageDisplay === '') {
      return ""
    }
  }
  if (typeof dosage === "string") {
    return `${dosage} [ERROR: Dosage data not in expected Dosage datatype.]`;
  }
  return "[ERROR: Dosage data not interpretable.]";
}

const displayStringFromFHIRDoseLimit = (doseLimit) => {
  if (doseLimit === undefined || doseLimit === null || doseLimit === '') {
    return ""
  }
  if (Array.isArray(doseLimit)) {
    return "[ERROR: DosageSafety.doseLimit data not interpretable.]";
  }
  if (typeof doseLimit === 'object') {
    let doseLimitDisplay = "Limit ";
    if (doseLimit.valueInteger) {
      doseLimitDisplay += doseLimit.valueInteger;
    }
    if (doseLimit.valueQuantity) {
      doseLimitDisplay += displayStringFromFHIRQuantity(doseLimit.valueQuantity);
    }
    if (doseLimit.valueExpression) {
      doseLimitDisplay += displayStringFromFHIRExpression(doseLimit.valueExpression);
    }
    doseLimitDisplay += " per " + doseLimit.scope;
    if (doseLimit.period) {
      doseLimitDisplay += " " + displayStringFromFHIRQuantity(doseLimit.period);
    }
    if (doseLimit.text) {
      doseLimitDisplay += " (" + doseLimit.text + ")";
    }
    if (doseLimitDisplay && typeof doseLimitDisplay === 'string') {
      return doseLimitDisplay;
    }
    if (doseLimitDisplay === '') {
      return ""
    }
  }
  if (typeof doseLimit === "string") {
    return `${doseLimit} [ERROR: DosageSafety.doseLimit data not in expected DosageSafety.doseLimit datatype.]`;
  }
  return "[ERROR: DosageSafety.doseLimit data not interpretable.]";
}

const getDosageConditionArrayDisplay = (dosageCondition) => {
  if (dosageCondition === undefined || dosageCondition === null || dosageCondition === '') {
    return ""
  }
  if (Array.isArray(dosageCondition))
    if (dosageCondition.length > 0) {
      let dosageConditionDisplay = dosageCondition.map(entry => {
        return displayStringFromFHIRDosageCondition(entry);
      }).join("; ");
      return dosageConditionDisplay;
    } else {
      return ""
    }
  if (typeof dosageCondition === 'object') {
    return displayStringFromFHIRDosageCondition(dosageCondition);
  }
  return "[ERROR: DosageCondition data not interpretable.]";
}

const displayStringFromFHIRDosageCondition = (dosageCondition) => {
  if (dosageCondition === undefined || dosageCondition === null || dosageCondition === '') {
    return ""
  }
  if (Array.isArray(dosageCondition)) {
    return "[ERROR: DosageCondition data not interpretable.]";
  }
  if (typeof dosageCondition === 'object') {

    if (dosageCondition.text) {
      return dosageCondition.text
    }
    let dosageConditionDisplay = "";
    if (dosageCondition.code) {
      dosageConditionDisplay = simpleDisplayStringFromFHIRCodeableConcept(dosageCondition.code);
    }
    if (dosageCondition.details) {
      dosageConditionDisplay += ", " + simpleDisplayStringFromFHIRCodeableConcept(dosageCondition.details);
    }
    if (dosageCondition.operation) {
      dosageConditionDisplay += ", SEE JSON for operational details";
    }
    if (dosageConditionDisplay && typeof dosageConditionDisplay === 'string') {
      return dosageConditionDisplay;
    }
    if (dosageConditionDisplay === '') {
      return ""
    }
  }
  if (typeof dosageCondition === "string") {
    return `${dosageCondition} [ERROR: DosageCondition data not in expected DosageCondition datatype.]`;
  }
  return "[ERROR: DosageCondition data not interpretable.]";
}

const displayStringFromFHIRDosageSafety = (dosageSafety) => {
  if (dosageSafety === undefined || dosageSafety === null || dosageSafety === '') {
    return ""
  }
  if (Array.isArray(dosageSafety)) {
    return "[ERROR: DosageSafety data not interpretable.]";
  }
  if (typeof dosageSafety === 'object') {
    let dosageSafetyDisplay = "";
    if (Array.isArray(dosageSafety.doseLimit) && dosageSafety.doseLimit.length > 0) {
      for (const doseLimit of dosageSafety.doseLimit) {
        let doseLimitDisplay = displayStringFromFHIRDoseLimit(doseLimit);
        if (doseLimitDisplay) {
          if (dosageSafetyDisplay) {
            dosageSafetyDisplay += ", ";
          }
          dosageSafetyDisplay += doseLimitDisplay;
        }
      }
    }
    if (dosageSafety.ifExceeded) {
      if (dosageSafetyDisplay) {
        dosageSafetyDisplay += "; ";
      }
      dosageSafetyDisplay += "if limit exceeded: " + dosageSafety.ifExceeded;
    }
    if (dosageSafetyDisplay && typeof dosageSafetyDisplay === 'string') {
      return dosageSafetyDisplay;
    }
    if (dosageSafetyDisplay === '') {
      return ""
    }
  }
  if (typeof dosageSafety === "string") {
    return `${dosageSafety} [ERROR: DosageSafety data not in expected DosageSafety datatype.]`;
  }
  return "[ERROR: DosageSafety data not interpretable.]";
}

const displayStringFromFHIRDosageStep = (dosageStep) => {
  if (dosageStep === undefined || dosageStep === null || dosageStep === '') {
    return ""
  }
  if (Array.isArray(dosageStep)) {
    return "[ERROR: DosageDetails.step data not interpretable.]";
  }
  if (typeof dosageStep === 'object') {
    let dosageStepDisplay = "";
    if (dosageStep.start) {
      dosageStepDisplay = "from " + displayStringFromFHIRRelativeTime(dosageStep.start);
    }
    if (dosageStep.end) {
      dosageStepDisplay = " until " + displayStringFromFHIRRelativeTime(dosageStep.end);
    }
    if (dosageStep.count > 1) {
      dosageStepDisplay += " " + dosageStep.count + " times";
    }
    if (Array.isArray(dosageStep.component) && dosageStep.component.length > 0) {
      for (const dosage of dosageStep.component) {
        if (dosageStepDisplay) {
          dosageStepDisplay += ": ";
        }
        dosageStepDisplay += displayStringFromFHIRDosage(dosage);
      }
    }
    if (dosageStep.safety) {
      if (dosageStepDisplay) {
        dosageStepDisplay += "; ";
      }
      dosageStepDisplay += "SAFETY DETAIL: " + displayStringFromFHIRDosageSafety(dosageStep.safety);
    }
    if (dosageStepDisplay && typeof dosageStepDisplay === 'string') {
      return dosageStepDisplay;
    }
    if (dosageStepDisplay === '') {
      return ""
    }
  }
  if (typeof dosageStep === "string") {
    return `${dosageStep} [ERROR: DosageDetails.step data not in expected DosageDetails.step datatype.]`;
  }
  return "[ERROR: DosageDetails.step data not interpretable.]";
}

const dosageConditionValueAllowedDatatypes = ['CodeableConcept', 'Range', 'integer', 'Quantity', 'Expression', 'Reference'];
const DosageConditionEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [dosageConditionValueDatatypeState, setDosageConditionValueDatatypeState] = useState('none');
  const [dosageConditionState, setDosageConditionState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDosageCondition = {
      'code': "", 'details': "", 'operation': "",
      'valueCodeableConcept': "", 'valueRange': "", 'valueReference': "",
      'valueInteger': "", 'valueQuantity': "", 'valueExpression': "",
      'text': ""
    };
    let startingDosageConditionValueDatatype = 'none';
    if (startingValue) {
      if (startingValue.extension) { startingDosageCondition.extension = startingValue.extension; }
      if (startingValue.code) { startingDosageCondition.code = startingValue.code; }
      if (startingValue.details) { startingDosageCondition.details = startingValue.details; }
      if (startingValue.operation) { startingDosageCondition.operation = startingValue.operation; }
      if (startingValue.valueRange) {
        startingDosageCondition.valueRange = startingValue.valueRange;
        startingDosageConditionValueDatatype = 'Range';
      }
      if (startingValue.valueReference) {
        startingDosageCondition.valueReference = startingValue.valueReference;
        startingDosageConditionValueDatatype = 'Reference';
      }
      if (startingValue.valueCodeableConcept) {
        startingDosageCondition.valueCodeableConcept = startingValue.valueCodeableConcept;
        startingDosageConditionValueDatatype = 'CodeableConcept';
      }
      if (startingValue.valueInteger) {
        startingDosageCondition.valueInteger = startingValue.valueInteger;
        startingDosageConditionValueDatatype = 'integer';
      }
      if (startingValue.valueQuantity) {
        startingDosageCondition.valueQuantity = startingValue.valueQuantity;
        startingDosageConditionValueDatatype = 'Quantity';
      }
      if (startingValue.valueExpression) {
        startingDosageCondition.valueExpression = startingValue.valueExpression;
        startingDosageConditionValueDatatype = 'Expression';
      }
      if (startingValue.text) { startingDosageCondition.text = startingValue.text; }
    }
    setDosageConditionState(JSON.parse(JSON.stringify(startingDosageCondition)));
    setDosageConditionValueDatatypeState(startingDosageConditionValueDatatype);
  }, []);

  useEffect((() => {
    if (Object.keys(dosageConditionState).length) {
      let newDosageCondition = {};
      if (dosageConditionState.code && Object.keys(dosageConditionState.code).length > 0) {
        newDosageCondition.code = dosageConditionState.code;
      }
      if (dosageConditionState.details && Object.keys(dosageConditionState.details).length > 0) {
        newDosageCondition.details = dosageConditionState.details;
      }
      if (dosageConditionState.operation) {
        newDosageCondition.operation = dosageConditionState.operation;
      }
      if (dosageConditionState.extension) { newDosageCondition.extension = dosageConditionState.extension; }
      if (dosageConditionValueAllowedDatatypes === 'integer' && !isNaN(dosageConditionState.valueInteger)) {
        newDosageCondition.valueInteger = dosageConditionState.valueInteger;
      }
      if (dosageConditionValueAllowedDatatypes === 'CodeableConcept' &&
        dosageConditionState.valueCodeableConcept && Object.keys(dosageConditionState.valueCodeableConcept).length) {
        newDosageCondition.valueCodeableConcept = dosageConditionState.valueCodeableConcept;
      }
      if (dosageConditionValueAllowedDatatypes === 'Range' &&
        dosageConditionState.valueRange && Object.keys(dosageConditionState.valueRange).length) {
        newDosageCondition.valueRange = dosageConditionState.valueRange;
      }
      if (dosageConditionValueAllowedDatatypes === 'Reference' &&
        dosageConditionState.valueReference && Object.keys(dosageConditionState.valueReference).length) {
        newDosageCondition.valueReference = dosageConditionState.valueReference;
      }
      if (dosageConditionValueAllowedDatatypes === 'Quantity' &&
        dosageConditionState.valueQuantity && Object.keys(dosageConditionState.valueQuantity).length) {
        newDosageCondition.valueQuantity = dosageConditionState.valueQuantity;
      }
      if (dosageConditionValueAllowedDatatypes === 'Expression' &&
        dosageConditionState.valueExpression && Object.keys(dosageConditionState.valueExpression).length) {
        newDosageCondition.valueExpression = dosageConditionState.valueExpression;
      }
      if (dosageConditionState.text) {
        newDosageCondition.text = dosageConditionState.text;
      }
      if (Object.keys(newDosageCondition).length === 0) {
        newDosageCondition = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosageCondition }; });
    }
  }), [dosageConditionState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosageCondition(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(dosageConditionState).length > 0) {
    return <>
      <div style={{ marginLeft: "24px" }}>
        <StringEntry elementName='text'
          fieldLabel='Description' startingValue={dosageConditionState.text}
          setResourceState={setDosageConditionState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='code'
          fieldLabel='Event'
          startingValue={dosageConditionState.code}
          setResourceState={setDosageConditionState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='details'
          fieldLabel='Event details'
          startingValue={dosageConditionState.details}
          setResourceState={setDosageConditionState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeEntry elementName='operation'
          fieldLabel='Operation code (how the value is tested)'
          startingValue={dosageConditionState.operation}
          setResourceState={setDosageConditionState}
          allowedValues={["eq", "ne", "in", "nin", "gt", "lt", "ge", "le", "sa", "eb", "ap"]}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
        <p style={{ marginBottom: "0px" }}><b>Criteria value:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='value[x]' allowedDatatypes={dosageConditionValueAllowedDatatypes}
            inTableCell={inTableCell} datatypeState={dosageConditionValueDatatypeState}
            setDatatypeState={setDosageConditionValueDatatypeState} />
          {dosageConditionValueDatatypeState === 'integer' &&
            <IntegerEntry elementName='valueInteger' fieldLabel={'Value as Number'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={dosageConditionState.valueInteger}
              setResourceState={setDosageConditionState} />}
          {dosageConditionValueDatatypeState === 'Quantity' &&
            <QuantityEntry elementName='valueQuantity' fieldLabel={'Value as Quantity'}
              inTableCell={inTableCell}
              startingValue={dosageConditionState.valueQuantity}
              setResourceState={setDosageConditionState} />}
          {dosageConditionValueDatatypeState === 'Expression' &&
            <ExpressionEntry elementName='valueExpression' fieldLabel={'Value as Expression'}
              inTableCell={inTableCell}
              startingValue={dosageConditionState.valueExpression}
              setResourceState={setDosageConditionState} />}
          {dosageConditionValueDatatypeState === 'Reference' &&
            <ExpressionEntry elementName='valueReference' fieldLabel={'Value as Reference'}
              inTableCell={inTableCell}
              startingValue={dosageConditionState.valueReference}
              setResourceState={setDosageConditionState} />}
          {dosageConditionValueDatatypeState === 'Range' &&
            <ExpressionEntry elementName='valueRange' fieldLabel={'Value as Range'}
              inTableCell={inTableCell}
              startingValue={dosageConditionState.valueRange}
              setResourceState={setDosageConditionState} />}
          {dosageConditionValueDatatypeState === 'CodeableConcept' &&
            <ExpressionEntry elementName='valueCodeableConcept' fieldLabel={'Value as CodeableConcept'}
              inTableCell={inTableCell}
              startingValue={dosageConditionState.valueCodeableConcept}
              setResourceState={setDosageConditionState} />}
        </div>
      </div>
    </>
  }
}

const displayStringFromFHIRDosageDetails = (dosageDetails) => {
  if (dosageDetails === undefined || dosageDetails === null || dosageDetails === '') {
    return ""
  }
  if (Array.isArray(dosageDetails)) {
    return "[ERROR: DosageDetails data not interpretable.]";
  }
  if (typeof dosageDetails === 'object') {
    if (dosageDetails.renderedInstruction) {
      return dosageDetails.renderedInstruction;
    }
    let dosageDetailsDisplay = "";
    if (dosageDetails.simple) {
      dosageDetailsDisplay = displayStringFromFHIRDosage(dosageDetails.simple);
    }
    if (Array.isArray(dosageDetails.step) && dosageDetails.step.length > 0) {
      for (const step of dosageDetails.step) {
        if (dosageDetailsDisplay) {
          dosageDetailsDisplay += "; ";
        }
        dosageDetailsDisplay += displayStringFromFHIRDosageStep(step);
      }
    }
    if (dosageDetails.safety) {
      if (dosageDetailsDisplay) {
        dosageDetailsDisplay += "; ";
      }
      dosageDetailsDisplay += "SAFETY DETAIL: " + displayStringFromFHIRDosageSafety(dosageDetails.safety);
    }
    if (dosageDetailsDisplay && typeof dosageDetailsDisplay === 'string') {
      return dosageDetailsDisplay;
    }
    if (dosageDetailsDisplay === '') {
      return ""
    }
  }
  if (typeof dosageDetails === "string") {
    return `${dosageDetails} [ERROR: DosageDetails data not in expected DosageDetails datatype.]`;
  }
  return "[ERROR: DosageDetails data not interpretable.]";
}

const DosageDetailsStepEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [dosageStepState, setDosageStepState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDosageStep = {
      'start': "", 'end': "", 'count': "", 'component': [], 'safety': ""
    };
    if (startingValue) {
      if (startingValue.extension) { startingDosageStep.extension = startingValue.extension; }
      if (startingValue.start) { startingDosageStep.start = startingValue.start; }
      if (startingValue.end) { startingDosageStep.end = startingValue.end; }
      if (startingValue.count) { startingDosageStep.count = startingValue.count; }
      if (startingValue.component) { startingDosageStep.component = startingValue.component; }
      if (startingValue.safety) { startingDosageStep.safety = startingValue.safety; }
    }
    setDosageStepState(JSON.parse(JSON.stringify(startingDosageStep)));
  }, []);

  useEffect((() => {
    if (Object.keys(dosageStepState).length) {
      let newDosageStep = {};
      if (dosageStepState.extension) { newDosageStep.extension = dosageStepState.extension; }
      if (dosageStepState.start && Object.keys(dosageStepState.start).length > 0) {
        newDosageStep.start = dosageStepState.start;
      }
      if (dosageStepState.end && Object.keys(dosageStepState.end).length > 0) {
        newDosageStep.end = dosageStepState.end;
      }
      if (dosageStepState.count) { newDosageStep.count = dosageStepState.count; }
      if (Array.isArray(dosageStepState.component) &&
        dosageStepState.component.length > 0) {
        newDosageStep.component = dosageStepState.component;
      }
      if (dosageStepState.safety && Object.keys(dosageStepState.safety).length > 0) {
        newDosageStep.safety = dosageStepState.safety;
      }
      if (Object.keys(newDosageStep).length === 0) {
        newDosageStep = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosageStep }; });
    }
  }), [dosageStepState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosageStep(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(dosageStepState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <RelativeTimeEntry elementName='start'
          fieldLabel='Start'
          startingValue={dosageStepState.start}
          setResourceState={setDosageStepState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <RelativeTimeEntry elementName='end'
          fieldLabel='End'
          startingValue={dosageStepState.end}
          setResourceState={setDosageStepState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <IntegerEntry elementName='count'
          fieldLabel='Number of times to do this step (if not 1)'
          startingValue={dosageStepState.count}
          setResourceState={setDosageStepState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='step'
          datatype='Dosage' fieldLabel='Dosage Detail for the Step'
          startingValue={dosageStepState.step}
          deletable={true} deletableArray={true}
          setResourceState={setDosageStepState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <DosageSafetyEntry elementName='safety'
          fieldLabel='Safety' startingValue={dosageStepState.safety}
          setResourceState={setDosageStepState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
      </div>
    </>
  }
}

const DosageDetailsEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [dosageDetailsState, setDosageDetailsState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDosageDetails = {
      'renderedInstruction': "", 'simple': "", 'step': [], 'safety': ""
    };
    if (startingValue) {
      if (startingValue.extension) { startingDosageDetails.extension = startingValue.extension; }
      if (startingValue.renderedInstruction) { startingDosageDetails.renderedInstruction = startingValue.renderedInstruction; }
      if (startingValue.simple) { startingDosageDetails.simple = startingValue.simple; }
      if (startingValue.step) { startingDosageDetails.step = startingValue.step; }
      if (startingValue.safety) { startingDosageDetails.safety = startingValue.safety; }
    }
    setDosageDetailsState(JSON.parse(JSON.stringify(startingDosageDetails)));
  }, []);

  useEffect((() => {
    if (Object.keys(dosageDetailsState).length) {
      let newDosageDetails = {};
      if (dosageDetailsState.extension) { newDosageDetails.extension = dosageDetailsState.extension; }
      if (dosageDetailsState.renderedInstruction) {
        newDosageDetails.renderedInstruction = dosageDetailsState.renderedInstruction;
      }
      if (dosageDetailsState.simple && Object.keys(dosageDetailsState.simple).length > 0) {
        newDosageDetails.simple = dosageDetailsState.simple;
      }
      if (Array.isArray(dosageDetailsState.step) &&
        dosageDetailsState.step.length > 0) {
        newDosageDetails.step = dosageDetailsState.step;
      }
      if (dosageDetailsState.safety && Object.keys(dosageDetailsState.safety).length > 0) {
        newDosageDetails.safety = dosageDetailsState.safety;
      }
      if (Object.keys(newDosageDetails).length === 0) {
        newDosageDetails = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosageDetails }; });
    }
  }), [dosageDetailsState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosageDetails(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(dosageDetailsState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <MarkdownEntry elementName='renderedInstruction'
          fieldLabel='Dosage Instruction'
          startingValue={dosageDetailsState.renderedInstruction}
          setResourceState={setDosageDetailsState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <DosageEntry elementName='simple'
          fieldLabel='Dosage Detail (if simple, single step)'
          startingValue={dosageDetailsState.simple}
          setResourceState={setDosageDetailsState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='step'
          datatype='DosageDetailsStep' fieldLabel='Dosage Detail Step'
          startingValue={dosageDetailsState.step}
          deletable={true} deletableArray={true}
          setResourceState={setDosageDetailsState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <DosageSafetyEntry elementName='safety'
          fieldLabel='Safety' startingValue={dosageDetailsState.safety}
          setResourceState={setDosageDetailsState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
      </div>
    </>
  }
}

const doseLimitValueAllowedDatatypes = ['integer', 'Quantity', 'Expression'];
const DosageSafetyDoseLimitEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [doseLimitValueDatatypeState, setDoseLimitValueDatatypeState] = useState('none');
  const [doseLimitState, setDoseLimitState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDoseLimit = {
      'valueInteger': "", 'valueQuantity': "", 'valueExpression': "",
      'scope': "", 'period': "", 'text': ""
    };
    let startingDoseLimitValueDatatype = 'none';
    if (startingValue) {
      if (startingValue.extension) { startingDoseLimit.extension = startingValue.extension; }
      if (startingValue.valueInteger) {
        startingDoseLimit.valueInteger = startingValue.valueInteger;
        startingDoseLimitValueDatatype = 'integer';
      }
      if (startingValue.valueQuantity) {
        startingDoseLimit.valueQuantity = startingValue.valueQuantity;
        startingDoseLimitValueDatatype = 'Quantity';
      }
      if (startingValue.valueExpression) {
        startingDoseLimit.valueExpression = startingValue.valueExpression;
        startingDoseLimitValueDatatype = 'Expression';
      }
      if (startingValue.scope) { startingDoseLimit.scope = startingValue.scope; }
      if (startingValue.period) { startingDoseLimit.period = startingValue.period; }
      if (startingValue.text) { startingDoseLimit.text = startingValue.text; }
    }
    setDoseLimitState(JSON.parse(JSON.stringify(startingDoseLimit)));
    setDoseLimitValueDatatypeState(startingDoseLimitValueDatatype);
  }, []);

  useEffect((() => {
    if (Object.keys(doseLimitState).length) {
      let newDoseLimit = {};
      if (doseLimitState.extension) { newDoseLimit.extension = doseLimitState.extension; }
      if (doseLimitValueDatatypeState === 'integer' && !isNaN(doseLimitState.valueInteger)) {
        newDoseLimit.valueInteger = doseLimitState.valueInteger;
      }
      if (doseLimitValueDatatypeState === 'Quantity' &&
        doseLimitState.valueQuantity && Object.keys(doseLimitState.valueQuantity).length) {
        newDoseLimit.valueQuantity = doseLimitState.valueQuantity;
      }
      if (doseLimitValueDatatypeState === 'Expression' &&
        doseLimitState.valueExpression && Object.keys(doseLimitState.valueExpression).length) {
        newDoseLimit.valueExpression = doseLimitState.valueExpression;
      }
      if (doseLimitState.scope) {
        newDoseLimit.scope = doseLimitState.scope;
      }
      if (doseLimitState.period && Object.keys(doseLimitState.period).length > 0) {
        newDoseLimit.period = doseLimitState.period;
      }
      if (doseLimitState.text) {
        newDoseLimit.text = doseLimitState.text;
      }
      if (Object.keys(newDoseLimit).length === 0) {
        newDoseLimit = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDoseLimit }; });
    }
  }), [doseLimitState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDoseLimit(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(doseLimitState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <p style={{ marginBottom: "0px" }}><b>Dose limit value:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='value[x]' allowedDatatypes={doseLimitValueAllowedDatatypes}
            inTableCell={inTableCell} datatypeState={doseLimitValueDatatypeState}
            setDatatypeState={setDoseLimitValueDatatypeState} />
          {doseLimitValueDatatypeState === 'integer' &&
            <IntegerEntry elementName='valueInteger' fieldLabel={'Dose limit as Number'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseLimitState.valueInteger} setResourceState={setDoseLimitState} />}
          {doseLimitValueDatatypeState === 'Quantity' &&
            <QuantityEntry elementName='valueQuantity' fieldLabel={'Dose limit as Quantity'}
              inTableCell={inTableCell}
              startingValue={doseLimitState.valueQuantity} setResourceState={setDoseLimitState} />}
          {doseLimitValueDatatypeState === 'Expression' &&
            <ExpressionEntry elementName='valueExpression' fieldLabel={'Dose limit as Expression'}
              inTableCell={inTableCell}
              startingValue={doseLimitState.valueExpression} setResourceState={setDoseLimitState} />}
        </div>
        <CodeEntry elementName='scope'
          fieldLabel='Scope of dose limit'
          startingValue={doseLimitState.scope}
          setResourceState={setDoseLimitState}
          allowedValues={["dosage", "period", "administration", "lifetime"]}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <PeriodEntry elementName='period'
          fieldLabel='Period (for scope)'
          startingValue={doseLimitState.period}
          setResourceState={setDoseLimitState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <StringEntry elementName='text'
          fieldLabel='Notes' startingValue={doseLimitState.text}
          setResourceState={setDoseLimitState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
      </div>
    </>
  }
}

const DosageSafetyEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [dosageSafetyState, setDosageSafetyState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDosageSafety = {
      'doseLimit': [], 'ifExceeded': ""
    };
    if (startingValue) {
      if (startingValue.extension) { startingDosageSafety.extension = startingValue.extension; }
      if (startingValue.doseLimit) { startingDosageSafety.doseLimit = startingValue.doseLimit; }
      if (startingValue.ifExceeded) { startingDosageSafety.ifExceeded = startingValue.ifExceeded; }
    }
    setDosageSafetyState(JSON.parse(JSON.stringify(startingDosageSafety)));
  }, []);

  useEffect((() => {
    if (Object.keys(dosageSafetyState).length) {
      let newDosageSafety = {};
      if (dosageSafetyState.extension) { newDosageSafety.extension = dosageSafetyState.extension; }
      if (dosageSafetyState.ifExceeded) {
        newDosageSafety.ifExceeded = dosageSafetyState.ifExceeded;
      }
      if (Array.isArray(dosageSafetyState.doseLimit) &&
        dosageSafetyState.doseLimit.length > 0) {
        newDosageSafety.doseLimit = dosageSafetyState.doseLimit;
      }
      if (Object.keys(newDosageSafety).length === 0) {
        newDosageSafety = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosageSafety }; });
    }
  }), [dosageSafetyState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosageSafety(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(dosageSafetyState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <ArrayEntry elementName='doseLimit'
          datatype='DosageSafetyDoseLimit' fieldLabel='Dose Limit'
          startingValue={dosageSafetyState.doseLimit}
          deletable={true} deletableArray={true}
          setResourceState={setDosageSafetyState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <StringEntry elementName='ifExceeded'
          fieldLabel='Instructions if exceeded' startingValue={dosageSafetyState.ifExceeded}
          setResourceState={setDosageSafetyState}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
      </div>
    </>
  }
}

const dosageDotAdditionalInstructionSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotAsNeededForSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotSiteSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotRouteSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotMethodSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const DosageEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [dosageState, setDosageState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDosage = {
      'condition': [], 'text': "", 'additionalInstruction': [], 'patientInstruction': "", 'timing': "",
      'asNeeded': null, 'asNeededFor': [], 'site': "", 'route': "", 'method': "", 'doseAndRate': [],
      'safety': ""
    }
    if (startingValue) {
      if (startingValue.extension) { startingDosage.extension = startingValue.extension; }
      if (startingValue.condition) { startingDosage.condition = startingValue.condition; }
      if (startingValue.text) { startingDosage.text = startingValue.text; }
      if (startingValue.additionalInstruction) { startingDosage.additionalInstruction = startingValue.additionalInstruction; }
      if (startingValue.patientInstruction) { startingDosage.patientInstruction = startingValue.patientInstruction; }
      if (startingValue.timing) { startingDosage.timing = startingValue.timing; }
      if (typeof startingValue.asNeeded === "boolean") { startingDosage.asNeeded = startingValue.asNeeded; }
      if (startingValue.asNeededFor) { startingDosage.asNeededFor = startingValue.asNeededFor; }
      if (startingValue.site) { startingDosage.site = startingValue.site; }
      if (startingValue.route) { startingDosage.route = startingValue.route; }
      if (startingValue.method) { startingDosage.method = startingValue.method; }
      if (startingValue.doseAndRate) { startingDosage.doseAndRate = startingValue.doseAndRate; }
      if (startingValue.safety) { startingDosage.safety = startingValue.safety; }
    }
    setDosageState(JSON.parse(JSON.stringify(startingDosage)));
  }, []);

  useEffect((() => {
    if (Object.keys(dosageState).length) {
      let newDosage = {};
      if (dosageState.extension) { newDosage.extension = dosageState.extension; }
      if (Array.isArray(dosageState.condition) && dosageState.condition.length) {
        newDosage.condition = dosageState.condition;
      }
      if (dosageState.text) { newDosage.text = dosageState.text; }
      if (Array.isArray(dosageState.additionalInstruction) &&
        dosageState.additionalInstruction.length) {
        newDosage.additionalInstruction = dosageState.additionalInstruction;
      }
      if (dosageState.patientInstruction) { newDosage.patientInstruction = dosageState.patientInstruction; }
      if (dosageState.timing && Object.keys(dosageState.timing).length) {
        newDosage.timing = dosageState.timing;
      }
      if (typeof dosageState.asNeeded === "boolean") {
        newDosage.asNeeded = dosageState.asNeeded;
      }
      if (Array.isArray(dosageState.asNeededFor) && dosageState.asNeededFor.length) {
        newDosage.asNeededFor = dosageState.asNeededFor;
      }
      if (dosageState.site && Object.keys(dosageState.site).length) {
        newDosage.site = dosageState.site;
      }
      if (dosageState.route && Object.keys(dosageState.route).length) {
        newDosage.route = dosageState.route;
      }
      if (dosageState.method && Object.keys(dosageState.method).length) {
        newDosage.method = dosageState.method;
      }
      if (Array.isArray(dosageState.doseAndRate) &&
        dosageState.doseAndRate.length) {
        newDosage.doseAndRate = dosageState.doseAndRate;
      }
      if (dosageState.safety && Object.keys(dosageState.safety).length) {
        newDosage.safety = dosageState.safety;
      }
      if (Object.keys(newDosage).length === 0) {
        newDosage = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosage }; });
    }
  }), [dosageState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosage(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(dosageState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <ArrayEntry elementName='condition'
          datatype='DosageCondition' fieldLabel='When dosage applies'
          startingValue={dosageState.condition}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <StringEntry elementName='text'
          fieldLabel='Dosage Instruction' startingValue={dosageState.text}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='additionalInstruction'
          datatype='CodeableConcept' fieldLabel='Additional Instruction' startingValue={dosageState.additionalInstruction}
          systemChoices={dosageDotAdditionalInstructionSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <StringEntry elementName='patientInstruction'
          fieldLabel='Patient Instruction' startingValue={dosageState.patientInstruction}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <TimingEntry elementName='timing'
          fieldLabel='Timing' startingValue={dosageState.timing}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <BooleanEntry elementName='asNeeded'
          fieldLabel='As Needed' startingValue={dosageState.asNeeded}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='asNeededFor'
          datatype='CodeableConcept' fieldLabel='As Needed For' startingValue={dosageState.asNeededFor}
          systemChoices={dosageDotAsNeededForSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='site'
          fieldLabel='Body Site' startingValue={dosageState.site}
          systemChoices={dosageDotSiteSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='route'
          fieldLabel='Route' startingValue={dosageState.route}
          systemChoices={dosageDotRouteSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='method'
          fieldLabel='Method' startingValue={dosageState.method}
          systemChoices={dosageDotMethodSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='doseAndRate'
          datatype='DoseAndRate' fieldLabel='Dose and Rate' startingValue={dosageState.doseAndRate}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <DosageSafetyEntry elementName='safety'
          fieldLabel='Safety limits' startingValue={dosageState.safety}
          setResourceState={setDosageState} inTableCell={inTableCell} />
      </div>
    </>
  }
};
const R5DosageEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [dosageState, setDosageState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDosage = {
      'sequence': "", 'text': "", 'additionalInstruction': [], 'patientInstruction': "", 'timing': "",
      'asNeeded': null, 'asNeededFor': [], 'site': "", 'route': "", 'method': "", 'doseAndRate': [],
      'maxDosePerPeriod': [], 'maxDosePerAdministration': "", 'maxDosePerLifetime': ""
    }
    if (startingValue) {
      if (startingValue.extension) { startingDosage.extension = startingValue.extension; }
      if (startingValue.sequence) { startingDosage.sequence = startingValue.sequence; }
      if (startingValue.text) { startingDosage.text = startingValue.text; }
      if (startingValue.additionalInstruction) { startingDosage.additionalInstruction = startingValue.additionalInstruction; }
      if (startingValue.patientInstruction) { startingDosage.patientInstruction = startingValue.patientInstruction; }
      if (startingValue.timing) { startingDosage.timing = startingValue.timing; }
      if (typeof startingValue.asNeeded === "boolean") { startingDosage.asNeeded = startingValue.asNeeded; }
      if (startingValue.asNeededFor) { startingDosage.asNeededFor = startingValue.asNeededFor; }
      if (startingValue.site) { startingDosage.site = startingValue.site; }
      if (startingValue.route) { startingDosage.route = startingValue.route; }
      if (startingValue.method) { startingDosage.method = startingValue.method; }
      if (startingValue.doseAndRate) { startingDosage.doseAndRate = startingValue.doseAndRate; }
      if (startingValue.maxDosePerPeriod) { startingDosage.maxDosePerPeriod = startingValue.maxDosePerPeriod; }
      if (startingValue.maxDosePerAdministration) { startingDosage.maxDosePerAdministration = startingValue.maxDosePerAdministration; }
      if (startingValue.maxDosePerLifetime) { startingDosage.maxDosePerLifetime = startingValue.maxDosePerLifetime; }
    }
    setDosageState(JSON.parse(JSON.stringify(startingDosage)));
  }, []);

  useEffect((() => {
    if (Object.keys(dosageState).length) {
      let newDosage = {};
      if (dosageState.extension) { newDosage.extension = dosageState.extension; }
      if (dosageState.sequence) { newDosage.sequence = dosageState.sequence; }
      if (dosageState.text) { newDosage.text = dosageState.text; }
      if (Array.isArray(dosageState.additionalInstruction) &&
        dosageState.additionalInstruction.length) {
        newDosage.additionalInstruction = dosageState.additionalInstruction;
      }
      if (dosageState.patientInstruction) { newDosage.patientInstruction = dosageState.patientInstruction; }
      if (dosageState.timing && Object.keys(dosageState.timing).length) {
        newDosage.timing = dosageState.timing;
      }
      if (typeof dosageState.asNeeded === "boolean") {
        newDosage.asNeeded = dosageState.asNeeded;
      }
      if (Array.isArray(dosageState.asNeededFor) && dosageState.asNeededFor.length) {
        newDosage.asNeededFor = dosageState.asNeededFor;
      }
      if (dosageState.site && Object.keys(dosageState.site).length) {
        newDosage.site = dosageState.site;
      }
      if (dosageState.route && Object.keys(dosageState.route).length) {
        newDosage.route = dosageState.route;
      }
      if (dosageState.method && Object.keys(dosageState.method).length) {
        newDosage.method = dosageState.method;
      }
      if (Array.isArray(dosageState.doseAndRate) &&
        dosageState.doseAndRate.length) {
        newDosage.doseAndRate = dosageState.doseAndRate;
      }
      if (Array.isArray(dosageState.maxDosePerPeriod) &&
        dosageState.maxDosePerPeriod.length) {
        newDosage.maxDosePerPeriod = dosageState.maxDosePerPeriod;
      }
      if (dosageState.maxDosePerAdministration && Object.keys(dosageState.maxDosePerAdministration).length) {
        newDosage.maxDosePerAdministration = dosageState.maxDosePerAdministration;
      }
      if (dosageState.maxDosePerLifetime && Object.keys(dosageState.maxDosePerLifetime).length) {
        newDosage.maxDosePerLifetime = dosageState.maxDosePerLifetime;
      }
      if (Object.keys(newDosage).length === 0) {
        newDosage = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosage }; });
    }
  }), [dosageState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosage(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(dosageState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <IntegerEntry elementName='sequence'
          fieldLabel='Dosage Sequence' startingValue={dosageState.sequence}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <StringEntry elementName='text'
          fieldLabel='Dosage Instruction' startingValue={dosageState.text}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='additionalInstruction'
          datatype='CodeableConcept' fieldLabel='Additional Instruction' startingValue={dosageState.additionalInstruction}
          systemChoices={dosageDotAdditionalInstructionSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <StringEntry elementName='patientInstruction'
          fieldLabel='Patient Instruction' startingValue={dosageState.patientInstruction}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <TimingEntry elementName='timing'
          fieldLabel='Timing' startingValue={dosageState.timing}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <BooleanEntry elementName='asNeeded'
          fieldLabel='As Needed' startingValue={dosageState.asNeeded}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='asNeededFor'
          datatype='CodeableConcept' fieldLabel='As Needed For' startingValue={dosageState.asNeededFor}
          systemChoices={dosageDotAsNeededForSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='site'
          fieldLabel='Body Site' startingValue={dosageState.site}
          systemChoices={dosageDotSiteSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='route'
          fieldLabel='Route' startingValue={dosageState.route}
          systemChoices={dosageDotRouteSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <CodeableConceptEntry elementName='method'
          fieldLabel='Method' startingValue={dosageState.method}
          systemChoices={dosageDotMethodSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='doseAndRate'
          datatype='DoseAndRate' fieldLabel='Dose and Rate' startingValue={dosageState.doseAndRate}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <ArrayEntry elementName='maxDosePerPeriod'
          datatype='Ratio' fieldLabel='Maximum Dose per Period' startingValue={dosageState.maxDosePerPeriod}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <QuantityEntry elementName='maxDosePerAdministration'
          fieldLabel='Maximum Dose per Administration' startingValue={dosageState.maxDosePerAdministration}
          setResourceState={setDosageState} dataEntryStyle={'SimpleQuantity'} inTableCell={inTableCell} />
        <QuantityEntry elementName='maxDosePerLifetime'
          fieldLabel='Maximum Dose per Lifetime' startingValue={dosageState.maxDosePerLifetime}
          setResourceState={setDosageState} dataEntryStyle={'SimpleQuantity'} inTableCell={inTableCell} />
      </div>
    </>
  }
};

const doseAndRateDotTypeValueSet = [
  { system: "https://terminology.hl7.org/5.0.0/CodeSystem-dose-rate-type.html", code: "calculated", display: "Calculated" },
  { system: "https://terminology.hl7.org/5.0.0/CodeSystem-dose-rate-type.html", code: "ordered", display: "Ordered" }
]
const doseAndRateDotDoseAllowedDatatypes = ['Range', 'SimpleQuantity', 'Expression'];
const doseAndRateDotRateAllowedDatatypes = ['Ratio', 'Range', 'SimpleQuantity', 'Expression'];
const DoseAndRateEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, startCollapsed }) => {

  const [doseAndRateState, setDoseAndRateState] = useState({});
  const [doseDatatypeState, setDoseDatatypeState] = useState('none');
  const [rateDatatypeState, setRateDatatypeState] = useState('none');
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingDoseAndRate = {
      'type': "", 'doseRange': "", 'doseQuantity': "", 'doseExpression': "",
      'rateRatio': "", 'rateRange': "", 'rateQuantity': "", 'rateExpression': ""
    };
    let startingDoseDatatype = 'none';
    let startingRateDatatype = 'none';
    if (startingValue) {
      if (startingValue.extension) { startingDoseAndRate.extension = startingValue.extension; }
      if (startingValue.type) { startingDoseAndRate.type = startingValue.type; }
      if (startingValue.doseRange) {
        startingDoseAndRate.doseRange = startingValue.doseRange;
        startingDoseDatatype = 'Range';
      }
      if (startingValue.doseQuantity) {
        startingDoseAndRate.doseQuantity = startingValue.doseQuantity;
        startingDoseDatatype = 'SimpleQuantity';
      }
      if (startingValue.doseExpression) {
        startingDoseAndRate.doseExpression = startingValue.doseExpression;
        startingDoseDatatype = 'Expression';
      }
      if (startingValue.rateRatio) {
        startingDoseAndRate.rateRatio = startingValue.rateRatio;
        startingRateDatatype = 'Ratio';
      }
      if (startingValue.rateRange) {
        startingDoseAndRate.rateRange = startingValue.rateRange;
        startingRateDatatype = 'Range';
      }
      if (startingValue.rateQuantity) {
        startingDoseAndRate.rateQuantity = startingValue.rateQuantity;
        startingRateDatatype = 'SimpleQuantity';
      }
      if (startingValue.rateExpression) {
        startingDoseAndRate.rateExpression = startingValue.rateExpression;
        startingRateDatatype = 'Expression';
      }
    }
    setDoseAndRateState(JSON.parse(JSON.stringify(startingDoseAndRate)));
    setDoseDatatypeState(startingDoseDatatype);
    setRateDatatypeState(startingRateDatatype);
  }, []);

  useEffect((() => {
    if (Object.keys(doseAndRateState).length) {
      let newDoseAndRate = {};
      if (doseAndRateState.extension) { newDoseAndRate.extension = doseAndRateState.extension; }
      if (doseAndRateState.type && Object.keys(doseAndRateState.type).length) {
        newDoseAndRate.type = doseAndRateState.type;
      }
      if (doseDatatypeState === 'Range' && doseAndRateState.doseRange && Object.keys(doseAndRateState.doseRange).length) {
        newDoseAndRate.doseRange = doseAndRateState.doseRange;
      }
      if (doseDatatypeState === 'SimpleQuantity' && doseAndRateState.doseQuantity && Object.keys(doseAndRateState.doseQuantity).length) {
        newDoseAndRate.doseQuantity = doseAndRateState.doseQuantity;
      }
      if (doseDatatypeState === 'Expression' && doseAndRateState.doseExpression && Object.keys(doseAndRateState.doseExpression).length) {
        newDoseAndRate.doseExpression = doseAndRateState.doseExpression;
      }
      if (rateDatatypeState === 'Ratio' && doseAndRateState.rateRatio && Object.keys(doseAndRateState.rateRatio).length) {
        newDoseAndRate.rateRatio = doseAndRateState.rateRatio;
      }
      if (rateDatatypeState === 'Range' && doseAndRateState.rateRange && Object.keys(doseAndRateState.rateRange).length) {
        newDoseAndRate.rateRange = doseAndRateState.rateRange;
      }
      if (rateDatatypeState === 'SimpleQuantity' && doseAndRateState.rateQuantity && Object.keys(doseAndRateState.rateQuantity).length) {
        newDoseAndRate.rateQuantity = doseAndRateState.rateQuantity;
      }
      if (rateDatatypeState === 'Expression' && doseAndRateState.rateExpression && Object.keys(doseAndRateState.rateExpression).length) {
        newDoseAndRate.rateExpression = doseAndRateState.rateExpression;
      }
      if (Object.keys(newDoseAndRate).length === 0) {
        newDoseAndRate = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDoseAndRate }; });
    }
  }), [doseAndRateState, doseDatatypeState, rateDatatypeState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && getDoseAndRateDisplay(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(doseAndRateState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <CodeableConceptEntry elementName='type' fieldLabel='Type'
          startingValue={doseAndRateState.type} setResourceState={setDoseAndRateState}
          valueSet={doseAndRateDotTypeValueSet}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} />
        <p style={{ marginBottom: "0px" }}><b>Dose:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='dose[x]' allowedDatatypes={doseAndRateDotDoseAllowedDatatypes}
            inTableCell={inTableCell} datatypeState={doseDatatypeState} setDatatypeState={setDoseDatatypeState} />
          {doseDatatypeState === 'Range' &&
            <RangeEntry elementName='doseRange' fieldLabel={'Range per Dose'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseAndRateState.doseRange} setResourceState={setDoseAndRateState} />}
          {doseDatatypeState === 'SimpleQuantity' &&
            <QuantityEntry elementName='doseQuantity' fieldLabel={'Quantity per Dose'}
              dataEntryStyle={"SimpleQuantity"} inTableCell={inTableCell}
              startingValue={doseAndRateState.doseQuantity} setResourceState={setDoseAndRateState} />}
          {doseDatatypeState === 'Expression' &&
            <ExpressionEntry elementName='doseExpression' fieldLabel={'Expression per Dose'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseAndRateState.doseExpression} setResourceState={setDoseAndRateState} />}
        </div>
        <p style={{ marginBottom: "0px" }}><b>Rate:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='rate[x]' allowedDatatypes={doseAndRateDotRateAllowedDatatypes}
            datatypeState={rateDatatypeState} setDatatypeState={setRateDatatypeState} />
          {rateDatatypeState === 'Ratio' &&
            <RatioEntry elementName='rateRatio' fieldLabel={'Ratio for Dose per Time'}
              startingValue={doseAndRateState.rateRatio} setResourceState={setDoseAndRateState} />}
          {rateDatatypeState === 'Range' &&
            <RangeEntry elementName='rateRange' fieldLabel={'Range for Dose per Time'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseAndRateState.rateRange} setResourceState={setDoseAndRateState} />}
          {rateDatatypeState === 'SimpleQuantity' &&
            <QuantityEntry elementName='rateQuantity' fieldLabel={'Quantity for Dose per Time'}
              dataEntryStyle={"SimpleQuantity"} inTableCell={inTableCell}
              startingValue={doseAndRateState.rateQuantity} setResourceState={setDoseAndRateState} />}
          {rateDatatypeState === 'Expression' &&
            <RangeEntry elementName='rateExpression' fieldLabel={'Expression for Dose per Time'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseAndRateState.rateExpression} setResourceState={setDoseAndRateState} />}
        </div>
      </div>
    </>
  }
}

const RatioEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle,
  numeratorValueSet, denominatorValueSet, startCollapsed, inTableCell }) => {
  let startingRatio = { numerator: "", denominator: "" };

  if (numeratorValueSet === "Age") {
    numeratorValueSet = UCUM.age;
  } else if (numeratorValueSet === "Duration") {
    numeratorValueSet = UCUM.duration;
  } else if (numeratorValueSet === "UCUM") {
    numeratorValueSet = UCUM.common;
  }
  if (denominatorValueSet === "Age") {
    denominatorValueSet = UCUM.age;
  } else if (denominatorValueSet === "Duration") {
    denominatorValueSet = UCUM.duration;
  } else if (denominatorValueSet === "UCUM") {
    denominatorValueSet = UCUM.common;
  }

  const [ratioState, setRatioState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    if (startingValue) {
      if (startingValue.extension) { startingRatio.extension = startingValue.extension; }
      if (startingValue.numerator) { startingRatio.numerator = startingValue.numerator; }
      if (startingValue.denominator) { startingRatio.denominator = startingValue.denominator; }
    }
    setRatioState(JSON.parse(JSON.stringify(startingRatio)));
  }, []);

  useEffect((() => {
    if (Object.keys(ratioState).length) {
      let newRatio = {};
      if (ratioState.extension) { newRatio.extension = ratioState.extension; }
      if (ratioState.numerator && Object.keys(ratioState.numerator).length) {
        newRatio.numerator = ratioState.numerator;
      }
      if (ratioState.denominator && Object.keys(ratioState.denominator).length) {
        newRatio.denominator = ratioState.denominator;
      }
      if (Object.keys(newRatio).length === 0) {
        newRatio = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newRatio }; });
    }
  }), [ratioState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && getRatioDisplay(startingRatio)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(ratioState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <QuantityEntry elementName='numerator'
          fieldLabel='Numerator' startingValue={ratioState.numerator} inTableCell={inTableCell}
          numeratorValueSet={numeratorValueSet} setResourceState={setRatioState} />
        <QuantityEntry elementName='denominator'
          fieldLabel='Denominator' startingValue={ratioState.denominator} dataEntryStyle='SimpleQuantity'
          inTableCell={inTableCell}
          denominatorValueSet={denominatorValueSet} setResourceState={setRatioState} />
      </div>
    </>
  }
};

export default DosageDetailsEntry;
export {
  displayStringFromFHIRDosage, DoseAndRateEntry, getRatioDisplay, RatioEntry, R5DosageEntry,
  DosageSafetyDoseLimitEntry, DosageDetailsStepEntry, DosageEntry, DosageConditionEntry
};